import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { DataGrid, DataGridColumnProps } from "@excent-one/react/data-grid";
import {
  ControlledComboBox,
  ControlledInput,
} from "@excent-one/react/form/inputs";
import { hyperlinkOpenIcon, plusIcon } from "@progress/kendo-svg-icons";
import {
  ContentLayout,
  ContentHeader,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
  ContentHeaderBackNavigation,
  ContentHeaderActions,
} from "@excent-one/react/layout/content";
import { FormLayout, FormSection, SearchFormLayout } from "@excent-one/react";
import { FormFieldSet } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-all";
import { useAuthContext } from "@excent-one/auth";
import {
  createMonthlyCoveragePlan,
  getAllAreas,
  getAllMonthlyCoveragePlanStatuses,
  getCoordinators,
  getMyDetails,
  getMyMembers,
  MonthlyCoveragePlanDto,
  MonthlyCoveragePlanSummaryDto,
  searchMonthlyCoveragePlans,
  validateByIdMonthlyCoveragePlans,
} from "@phrmpc/infrastructure/phrmpc";
import {
  FilterLogic,
  FilterOperator,
  MONTHS_OPTIONS,
  YEARS_OPTIONS,
} from "@phrmpc/shared";
import { useQuery } from "@tanstack/react-query";
import { notification } from "@excent-one/react/notification";
import { usePagedGrid } from "@phrmpc/shared/hooks/usePagedGrid";
import { isEmpty } from "lodash";
import { getServerErrorMessage } from "../utils/ErrorMessages";

interface SearchFormValues {
  monthId: number | null;
  year: number | null;
  coordinatorId: string;
  coordinatorName: string;
  areaId: number | null;
  statusId: number | null;
}

const buildFilter = (values: SearchFormValues) => {
  const filters: { field: string; operator: number; value: unknown }[] = [];

  if (values.monthId) {
    filters.push({
      field: "monthId",
      operator: FilterOperator.Equals,
      value: values.monthId,
    });
  }
  if (values.year) {
    filters.push({
      field: "year",
      operator: FilterOperator.Equals,
      value: values.year,
    });
  }
  if (!isEmpty(values.coordinatorId)) {
    filters.push({
      field: "coordinatorId",
      operator: FilterOperator.Equals,
      value: values.coordinatorId,
    });
  }
  if (values.statusId != null) {
    filters.push({
      field: "statusId",
      operator: FilterOperator.Equals,
      value: values.statusId,
    });
  }
  if (values.areaId != null) {
    filters.push({
      field: "areaId",
      operator: FilterOperator.Equals,
      value: values.areaId,
    });
  }
  if (filters.length === 0) return null;
  return { logic: FilterLogic.And, filters } as const;
};

const MonthlyCoveragePlanSearchForm = () => {
  const navigate = useNavigate();
  const [{ currentUser }, { isInAnyRole }] = useAuthContext();
  const [creating, setCreating] = useState(false);

  const isUserCoordinator = isInAnyRole("Coordinator");
  const isUserAreaHead = isInAnyRole("Area Head");
  const isUserManager = isInAnyRole("Manager");

  const { data: statusOptions } = useQuery({
    queryKey: ["monthly-coverage-plan-statuses"],
    queryFn: () => getAllMonthlyCoveragePlanStatuses(),
  });

  const { data: areaOptions } = useQuery({
    queryKey: ["areas"],
    queryFn: () => getAllAreas(),
  });

  const { data: myDetails } = useQuery({
    queryKey: ["my-details", currentUser?.id],
    queryFn: () => getMyDetails(),
  });

  const { data: memberCoordinators } = useQuery({
    queryKey: ["my-members", currentUser?.id],
    queryFn: () =>
      getMyMembers({
        payload: {
          pageSize: 10000,
        },
      } as any),
  });

  const { data: allCoordinatorsAndAreaHeads } = useQuery({
    queryKey: ["all-coordinators", currentUser?.id],
    queryFn: () =>
      getCoordinators({
        payload: {
          pageSize: 10000,
        },
      } as any),
  });

  const defaultSearchValues: SearchFormValues = {
    monthId: null,
    year: null,
    coordinatorId: myDetails?.id ?? "",
    coordinatorName: myDetails?.fieldName ?? "",
    areaId: null,
    statusId: null,
  };

  const { control, getValues, reset } = useForm<SearchFormValues>({
    defaultValues: defaultSearchValues,
  });

  const coordinatorOptions = useMemo(() => {
    const members = isUserManager
      ? (allCoordinatorsAndAreaHeads?.items ?? [])
      : isUserAreaHead
        ? (memberCoordinators?.items ?? [])
        : [];
    if (members.length === 0 || isUserCoordinator) {
      return [];
    }

    if (!myDetails) {
      return members;
    }
    return [
      {
        id: myDetails.id,
        fieldName: myDetails.fieldName,
      },
      ...members,
    ];
  }, [
    memberCoordinators?.items,
    allCoordinatorsAndAreaHeads?.items,
    myDetails,
  ]);

  useEffect(() => {
    if (!myDetails) {
      return;
    }

    const coordinatorId = isUserAreaHead
      ? (coordinatorOptions?.[0]?.id ?? "")
      : (myDetails?.id ?? "");

    const coordinatorName = isUserAreaHead
      ? (coordinatorOptions?.[0]?.fieldName ?? "")
      : (myDetails?.fieldName ?? "");

    const initialSearchValues: SearchFormValues = {
      monthId: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      coordinatorId,
      coordinatorName,
      areaId: null,
      statusId: null,
    };
    reset(initialSearchValues);
    handleSearch();
  }, [myDetails, coordinatorOptions, reset]);

  const [appliedFilters, setAppliedFilters] =
    useState<SearchFormValues>(defaultSearchValues);
  const { skip, pageSize, pageNumber, pageable, onPageChange, resetPaging } =
    usePagedGrid();

  const { data: pagedResult, isLoading, refetch } = useQuery({
    queryKey: ["monthly-coverage-plans", appliedFilters, skip, pageSize],
    queryFn: () =>
      searchMonthlyCoveragePlans({
        payload: {
          pageNumber,
          pageSize,
          filter: buildFilter(appliedFilters) as any,
        },
      } as any),
    refetchOnMount: "always",
    staleTime: 0,
  });

  const handleSearch = () => {
    const values = getValues();
    if (buildFilter(values) === null) {
      notification.error("Please specify at least one search criterion.");
      return;
    }
    resetPaging();
    setAppliedFilters(values);
    refetch();
  };

  const handleClear = () => {
    const coordinatorId =
      coordinatorOptions.length > 0 ? "" : (myDetails?.id ?? "");
    const coordinatorName =
      coordinatorOptions.length > 0 ? "" : (myDetails?.fieldName ?? "");
    const clearSearchValues: SearchFormValues = {
      monthId: null,
      year: null,
      coordinatorId,
      coordinatorName,
      areaId: null,
      statusId: null,
    };

    reset(clearSearchValues);
    resetPaging();
    setAppliedFilters(clearSearchValues);
    refetch();
  };

  const gridData = (pagedResult?.items ?? []).map((row) => ({
    ...row,
  }));

  const COLUMNS: DataGridColumnProps<MonthlyCoveragePlanSummaryDto>[] = [
    { field: "id", title: "MCP ID", readOnly: true, hidden: true },
    { field: "monthName", title: "Month", readOnly: true },
    { field: "coordinatorName", title: "Coordinator Name", readOnly: true },
    { field: "statusName", title: "Status", readOnly: true },
  ];

  const handleCreate = async () => {
    if (!myDetails) {
      notification.error("Unable to determine current user. Please try again.");
      return;
    }
    setCreating(true);
    try {
      const now = new Date();
      now.setDate(1);
      now.setMonth(now.getMonth() + 1);
      const nextMonth = now.getMonth() + 1;
      const nextYear = now.getFullYear();

      const validation: any = await validateByIdMonthlyCoveragePlans(
        myDetails.id ?? "",
        nextYear,
        nextMonth,
      );

      if (!validation.isExisting) {
        const defaultValues: MonthlyCoveragePlanDto = {
          coordinatorId: myDetails?.id,
          coordinatorName: myDetails?.fieldName,
          monthId: nextMonth,
          monthName: now.toLocaleString("default", { month: "long" }),
          areaId: myDetails.areaId,
          areaName: myDetails.areaName,
          year: nextYear,
          statusId: 1,
          statusName: "Draft",
          mcpSiteAssignments: [],
        };

        const created = await createMonthlyCoveragePlan({
          payload: defaultValues,
        });
        navigate({
          to: "/deployment/monthly-coverage-plan/$id/edit",
          params: { id: created.id! },
          replace: true,
        });
      } else {
        notification.info(
          "Monthly Coverage Plan for Month and Year already exists.",
        );
      }
    } catch (e) {
      console.error(e);
      notification.error(
        getServerErrorMessage(
          e,
          "Unable to create monthly coverage plan. Please try again.",
        ),
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <ContentLayout>
      <ContentHeader>
        <ContentHeaderBackNavigation />
        <ContentHeaderTitle title="Search Monthly Coverage Plans" />
        <ContentHeaderBreadcrumbs />
        <SearchFormLayout
          withAdvancedFilters
          buttonProps={{
            search: { type: "submit", onClick: handleSearch },
            clear: { type: "button", onClick: handleClear },
          }}
        >
          <FormFieldSet cols={2}>
            <ControlledComboBox
              control={control}
              name="monthId"
              label="Month"
              data={MONTHS_OPTIONS}
              dataItemKey="id"
              textField="name"
              wrapperProps={{ className: "basic-search-field" }}
            />
            <ControlledComboBox
              control={control}
              name="year"
              label="Year"
              data={YEARS_OPTIONS}
              dataItemKey="id"
              textField="name"
              wrapperProps={{ className: "basic-search-field" }}
            />
            {coordinatorOptions?.length ? (
              <ControlledComboBox
                control={control}
                name="coordinatorId"
                label="Coordinator"
                data={coordinatorOptions}
                dataItemKey="id"
                textField="fieldName"
                wrapperProps={{ className: "basic-search-field" }}
              />
            ) : (
              <ControlledInput
                control={control}
                name="coordinatorName"
                label="Coordinator Name"
                placeholder="Search by coordinator name"
                wrapperProps={{ className: "basic-search-field" }}
                readOnly
              />
            )}
            {areaOptions && (
              <ControlledComboBox
                control={control}
                name="areaId"
                label="Area"
                data={areaOptions}
                dataItemKey="id"
                textField="name"
              />
            )}
            {statusOptions && (
              <ControlledComboBox
                control={control}
                name="statusId"
                label="Status"
                data={statusOptions}
                dataItemKey="id"
                textField="name"
              />
            )}
          </FormFieldSet>
        </SearchFormLayout>
        <ContentHeaderActions>
          <Button
            themeColor="primary"
            svgIcon={plusIcon}
            disabled={creating}
            onClick={handleCreate}
            hidden={isUserManager}
          >
            Create Monthly Coverage Plan Request
          </Button>
        </ContentHeaderActions>
      </ContentHeader>
      <FormLayout>
        <FormSection title="Results">
          <DataGrid
            data={gridData}
            dataItemKey="id"
            columns={COLUMNS}
            canAdd={false}
            pageable={pageable}
            skip={skip}
            take={pageSize}
            total={pagedResult?.totalItems ?? 0}
            onPageChange={onPageChange}
            noRecordsMessage="No matching monthly coverage plans found."
            style={{ maxHeight: "500px" }}
            scrollable="virtual"
            loading={isLoading}
            commands={[
              {
                command: "view",
                text: "View",
                svgIcon: hyperlinkOpenIcon,
              },
            ]}
            onCommandClicked={{
              view: (e) => {
                if (e.dataItem == null) return;
                navigate({ to: `${e.dataItem.id}/display` });
              },
            }}
          />
        </FormSection>
      </FormLayout>
    </ContentLayout>
  );
};

export default MonthlyCoveragePlanSearchForm;
